import { useState } from 'react';
import {
  FileText, HelpCircle,
  ShieldCheck
} from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { type CatalogEntry } from '../api';

interface TemplateStudioHeaderProps {
  catalog: CatalogEntry[] | undefined;
  isLoading?: boolean;
  activeDepartment: string;
}

export function TemplateStudioHeader({
  catalog,
  isLoading = false,
  activeDepartment,
}: TemplateStudioHeaderProps) {
  const [guideOpen, setGuideOpen] = useState(false);

  // Compute stats across the catalog
  const totalTemplates = catalog?.length || 0;
  const customCount = catalog?.reduce((acc, item) => {
    const hasCustom = item.customTemplates.some(
      (t) => (t.department || '') === activeDepartment
    );
    return acc + (hasCustom ? 1 : 0);
  }, 0) || 0;
  const publishedCount = catalog?.reduce((acc, item) => {
    const pub = item.customTemplates.some(
      (t) => (t.department || '') === activeDepartment && t.status === 'published'
    );
    return acc + (pub ? 1 : 0);
  }, 0) || 0;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        eyebrow="People"
        title="HR Document Templates"
        description="Design, customise and publish offer letters, NDAs and appointment letters."
        actions={
          <Button size="sm" variant="outline" onClick={() => setGuideOpen(true)} title="View token variable and letterhead guide">
            <HelpCircle className="h-4 w-4 mr-1.5" />
            Token Guide
          </Button>
        }
      />
      <StatGrid cols={3}>
        <StatCard label="Template types" value={totalTemplates} icon={FileText} tone="primary" loading={isLoading} />
        <StatCard label="Customised" value={`${customCount} / ${totalTemplates}`} hint="Edited from the default" tone="info" loading={isLoading} />
        <StatCard label="Live published" value={publishedCount} hint="In use for new documents" tone="success" loading={isLoading} />
      </StatGrid>

      {/* Guide & Tokens Modal */}
      <Modal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="HR Document Templates & Branding Guide"
        size="lg"
      >
        <div className="space-y-6 text-on-surface">
          <div className="rounded-xl bg-primary/5 p-4 border border-primary/20 flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-on-surface">
                Dynamic Letterhead & Legal Compliance
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every HR document generated in ZYNX is automatically wrapped with your company’s active <strong>Letterhead Banner</strong>, <strong>Legal Entity Footer</strong>, and <strong>Authorized Signatory</strong> details configured in <strong>HR Branding</strong>.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-on-surface">How Variable Tokens Work</h4>
            <p className="text-xs text-muted-foreground">
              Insert Handlebars tokens like <code className="bg-surface-variant px-1.5 py-0.5 rounded text-primary font-mono">{`{{candidate_name}}`}</code> or <code className="bg-surface-variant px-1.5 py-0.5 rounded text-primary font-mono">{`{{salary_table_html}}`}</code> into your template body. When generating an offer or appointment letter, ZYNX replaces these tokens with live employee and payroll data.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="rounded-lg border border-outline p-3 bg-surface">
                <div className="text-xs font-semibold text-on-surface mb-1">
                  1. Scope Hierarchy (Company vs Department)
                </div>
                <p className="text-xs text-muted-foreground">
                  You can publish a company-wide default template, or customize specific templates for individual departments (e.g. <em>Engineering</em> vs <em>Sales</em>).
                </p>
              </div>

              <div className="rounded-lg border border-outline p-3 bg-surface">
                <div className="text-xs font-semibold text-on-surface mb-1">
                  2. ZYNX Standard vs Custom Drafts
                </div>
                <p className="text-xs text-muted-foreground">
                  If you ever want to revert your customizations, click <strong>Reset to Standard</strong> to restore ZYNX’s legally vetted default template.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-outline">
            <Button size="sm" variant="primary" onClick={() => setGuideOpen(false)}>
              Got it
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
